import Evaporate from 'evaporate'
import sparkMD5 from 'spark-md5'
import sha256 from 'js-sha256'

export function createUploader(
  signerUrl,
  accessKey,
  bucket,
  onlyRetryForSameFileName = false
) {
  const obj = Evaporate.create({
    signerUrl: signerUrl,
    aws_key: accessKey,
    bucket: bucket,
    cloudfront: true,
    computeContentMd5: true,
    cryptoMd5Method: (d) => btoa(sparkMD5.ArrayBuffer.hash(d, true)),
    cryptoHexEncodedHash256: sha256,
    partSize: 20 * 1024 * 1024,
    s3Acceleration: true,
    s3FileCacheHoursAgo: 24,
    onlyRetryForSameFileName,
  })
  return obj
}

export function getFileMeta(file) {
  return new Promise(function (resolve) {
    let meta = {
      name: file.name,
      size: file.size,
      errors: [],
    }

    // No other validation matters if the file is empty.
    if (file.size == 0) {
      meta['errors'].push('The file is empty.')
      resolve(meta)
      return
    }

    if (file.type.match(/^image\//)) {
      getImageDimensions(file).then(function ([width, height, error]) {
        meta['width'] = width
        meta['height'] = height
        if (error) meta['errors'].push(error)
        resolve(meta)
      })
    } else {
      resolve(meta)
    }
  })
}

function getImageDimensions(file) {
  return new Promise(function (resolve) {
    // HEIC/HEIF files are not supported by browser's Image object but are valid image files
    // that the backend can process, so skip dimension validation for these formats
    const isHeicHeif =
      /\.(heic|heif)$/i.test(file.name) ||
      ['image/heic', 'image/heif'].includes(file.type)

    if (isHeicHeif) {
      resolve([null, null, null]) // No dimensions needed for HEIC/HEIF
      return
    }

    let img = new Image()
    img.onload = function () {
      resolve([this.width, this.height, null])
    }
    img.onerror = function () {
      resolve([null, null, 'Image does not seem to be valid.'])
    }
    img.src = URL.createObjectURL(file)
  })
}

export function calculateRemain(total, done, remain_s, uploadStartedAt) {
  const elapsed_ms = new Date() - uploadStartedAt
  const percentage = Math.floor((done * 100) / total)

  if (percentage > 10 || elapsed_ms > 10000) {
    if (total == done) {
      return 'Finalizing'
    }

    // Resuming uploads sometimes reports negative time remaining.
    if (remain_s <= 0) return ''

    let [remain_m, remain_h] = Array.from([0, 0])
    if (remain_s > 60) {
      ;[remain_m, remain_s] = Array.from([
        Math.floor(remain_s / 60),
        remain_s % 60,
      ])
    }
    if (remain_m > 60) {
      ;[remain_h, remain_m] = Array.from([
        Math.floor(remain_m / 60),
        remain_m % 60,
      ])
    }

    let remain = ''
    if (remain_h > 0) {
      remain += `${remain_h}h `
    }
    if (remain_m > 0 || remain_h > 0) {
      remain += `${remain_m}m `
    }

    // Don't add seconds when >= 1 hour is remaining
    if (remain_h < 1) remain += `${remain_s}s`

    return remain
  } else {
    return ''
  }
}

export function deleteAssetFromDB(assetId) {
  // Import the helper function dynamically to avoid circular imports
  return import('~/common/helpers/csrf').then(({ getCsrfParamsAsJSON }) => {
    return fetch(`/dropzone/assets/${assetId}`, {
      method: 'DELETE',
      body: getCsrfParamsAsJSON(),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to delete asset: ${response.statusText}`)
      }
      return response.json()
    })
  })
}

export function createAWSUploadHandler(config) {
  const {
    signerUrl,
    awsAccessKeyId,
    bucket,
    path,
    objectName,
    onProgress,
    onComplete,
    onError,
    onCancel,
  } = config

  return {
    uploadFile: async (file) => {
      const uploader = createUploader(signerUrl, awsAccessKeyId, bucket)
      const fullPath = path.replaceAll('${filename}', file.name)
      const uploadStartedAt = new Date()

      try {
        const [evaporate, fileMeta] = await Promise.all([
          uploader,
          getFileMeta(file),
        ])

        if (fileMeta.errors.length > 0) {
          console.log('Validation failed: ' + fileMeta.errors)
          onError?.(
            'We could not upload your file. ' + fileMeta.errors.join(' '),
            file
          )
          return
        }

        const uploadPromise = evaporate.add({
          file: file,
          name: fullPath,
          complete: (xhr, awsObjectKey) => {
            handleUploadComplete(
              awsObjectKey,
              fileMeta,
              file,
              bucket,
              objectName,
              onComplete,
              onError
            )
          },
          error: (mssg) => {
            console.log('Error', mssg)
            onError?.(mssg, file)
          },
          paused: () => console.log('Paused'),
          pausing: () => console.log('Pausing'),
          resumed: () => console.log('Resumed'),
          cancelled: () => {
            console.log('Cancelled')
            onCancel?.(file)
          },
          warn: (mssg) => console.log('Warning', mssg),
          progress: (percent, stats) => {
            const total = stats.fileSize
            const done = stats.totalUploaded
            let percentage = Math.floor((done * 100) / total)
            const remaining_s = stats.secondsLeft

            // We show 4% as the minimum progress bar width
            // Once this is complete we upload the file to S3 and it takes some time, so limit the progress to 95%
            if (percentage > 4) {
              percentage = Math.min(percentage, 95)
            }

            const remain = calculateRemain(
              total,
              done,
              remaining_s,
              uploadStartedAt
            )

            onProgress?.({
              percentage,
              total,
              done,
              remaining: remain,
              file,
            })
          },
        })

        return {
          promise: uploadPromise,
          cancel: () => evaporate.cancel(bucket + '/' + fullPath),
        }
      } catch (error) {
        onError?.(error.message, file)
        throw error
      }
    },
  }
}

function handleUploadComplete(
  awsObjectKey,
  fileMeta,
  file,
  bucket,
  objectName,
  onComplete,
  onError
) {
  import('~/common/helpers/csrf').then(({ getCsrfParams }) => {
    awsObjectKey = decodeURIComponent(awsObjectKey)
    let [param, value] = getCsrfParams()

    let postData = {
      s3_key: awsObjectKey,
      s3_bucket: bucket,
      filename: fileMeta.name,
      size: fileMeta.size,
      width: fileMeta.width,
      height: fileMeta.height,
      object_name: objectName,
    }

    postData[param] = value

    const xhr = new XMLHttpRequest()
    xhr.onreadystatechange = function () {
      if (this.readyState == 4) {
        let response
        try {
          response = JSON.parse(xhr.responseText)
        } catch (e) {
          response = { error: 'Error in processing the file' }
          console.log('error in XHTTP', xhr)
        }

        if (this.status == 200) {
          onComplete?.(response, file)
        } else {
          onError?.(response.error, file)
        }
      }
    }

    xhr.open('POST', '/dropzone/assets/upload', true)
    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
    xhr.send(JSON.stringify(postData))
  })
}
